import { Box, styled } from '@mui/joy';

import { CustomColor } from 'theme/enums';

interface IProps {
  selectsRange?: boolean;
  isDisabled: boolean;
}

const propsToSkip: PropertyKey[] = ['selectsRange', 'isDisabled'];
const shouldForwardProp = (propName: PropertyKey) => !propsToSkip.includes(propName);

const DatePickerWrapper = styled(Box, { shouldForwardProp })<IProps>(
  ({ theme, selectsRange, isDisabled }) => ({
    position: 'relative',
    marginLeft: -1,
    borderLeft: selectsRange ? `1px solid ${theme.palette.neutral[100]}` : 'inherit',
    fontSize: 'inherit',

    '.react-datepicker': {
      border: `1px solid ${theme.palette.ag.input.borderColor}`,
      borderRadius: 3,
      backgroundColor: theme.palette.background.body,
      color: theme.palette.text.primary,
    },

    '.react-datepicker__navigation-icon::before': {
      borderColor: theme.palette.text.primary,
    },

    '.react-datepicker-popper[data-placement^="bottom"] .react-datepicker__triangle::after': {
      borderBottomColor: theme.palette.background.body,
    },

    '.react-datepicker__input-container input': {
      width: '100%',
      alignItems: 'center',
      backgroundColor: theme.palette.background.body,
      borderColor: theme.palette.ag.input.borderColor,
      borderRadius: 3,
      borderStyle: 'solid',
      borderWidth: 1,
      boxShadow: 'none',
      display: 'flex',
      justifyContent: 'space-between',
      minHeight: 32,
      outline: 0,
      position: 'relative',
      transition: 'all 100ms',
      boxSizing: 'border-box',
      padding: '3px 10px',
      cursor: 'pointer',
      color: 'inherit',
      border: `1px solid ${theme.palette.ag.input.borderColor}`,
      fontSize: 13,

      '&:focus': {
        margin: '0 -1px',
        borderColor: theme.palette.focusVisible ?? theme.palette.primary.solidBg,
        borderWidth: 2,
      },

      ...(isDisabled && {
        border: 'none',
        cursor: 'not-allowed',
        fontSize: 'inherit',
        opacity: 0.5,

        '&:focus, &:hover': {
          border: 'none',
        },
      }),
    },

    '.react-datepicker__current-month': {
      paddingBottom: 5,
      paddingTop: 5,
      color: theme.palette.text.primary,
    },

    '.react-datepicker__day-name': {
      fontWeight: 600,
      color: theme.palette.text.primary,
    },

    '.react-datepicker__header': {
      border: 'none',
      fontWeight: 500,
      backgroundColor: 'transparent',
      color: theme.palette.text.primary,
    },

    '.react-datepicker__month': {
      margin: 0,
    },

    '.react-datepicker__week': {
      marginTop: -3,
      fontWeight: 400,
    },

    '.react-datepicker__day': {
      margin: 0,
      marginBottom: 3,
      width: 30,
      padding: '1px 3px',
      color: theme.palette.text.primary,
    },

    '.react-datepicker__day--disabled': {
      color: theme.palette.text.tertiary,
    },

    '.react-datepicker__day--in-range': {
      borderRadius: 0,
      backgroundColor: theme.palette.primary.solidBg,
      border: 'none',
      color: theme.palette.common[CustomColor.WHITE],
    },

    '.react-datepicker__day--in-selecting-range': {
      borderRadius: 0,
      backgroundColor: theme.palette.primary.solidBg,
      color: theme.palette.common[CustomColor.WHITE],
    },

    '.react-datepicker__day--in-selecting-range.react-datepicker__day--selected': {
      backgroundColor: theme.palette.primary.solidBg,
      borderBottomLeftRadius: 5,
      color: theme.palette.common.white,
    },

    '.react-datepicker__day--selected': {
      backgroundColor: theme.palette.primary.solidBg,
      color: theme.palette.common.white,
    },

    '.react-datepicker__day--keyboard-selected': {
      backgroundColor: theme.palette.neutral[500],
      color: theme.palette.common.white,
    },

    '.react-datepicker__day--range-start': {
      backgroundColor: theme.palette.primary.solidBg,
      borderTopLeftRadius: 5,
      borderBottomLeftRadius: 5,
      color: theme.palette.common.white,
    },

    '.react-datepicker__day--range-end': {
      backgroundColor: theme.palette.primary.solidBg,
      borderTopRightRadius: 5,
      borderBottomRightRadius: 5,
      color: theme.palette.common.white,
    },

    '.react-datepicker__day--outside-month': {
      visibility: 'hidden',
    },

    // eslint-disable-next-line max-len
    '.react-datepicker__year-dropdown-container--select, .react-datepicker__month-dropdown-container--select, .react-datepicker__month-year-dropdown-container--select,  .react-datepicker__year-dropdown-container--scroll, .react-datepicker__month-dropdown-container--scroll, .react-datepicker__month-year-dropdown-container--scroll':
      {
        display: 'inline-block',
        margin: '0 12px',
      },

    '.react-datepicker__close-icon::after': {
      backgroundColor: 'inherit',
      color: 'inherit',
      fontSize: 14,
      padding: 4,
    },
  })
);

export default DatePickerWrapper;
